/** @format */

import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Menu from '@mui/icons-material/Menu';
import Clear from '@mui/icons-material/Clear';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Logout from '@mui/icons-material/Logout';
import Drawer from '@mui/material/Drawer';
import intl from 'react-intl-universal';

import GlobalMenuList from './GlobalMenuList';
import GlobalMenuItem from './GlobalMenuItem';
import GlobalMenuIcon from './GlobalMenuIcon';
import GlobalMenuGroup from './GlobalMenuGroupContainer';
import {
  PREREGISTER_APPLICANTS_URL,
  CASES_URL,
  DASHBOARD_URL,
  DOCUMENTS_URL,
  UPLOADS,
  HELP_URL,
  LOGOUT_URL,
  USERS_URL,
  ENGAGEMENTS_URL,
  ENGAGEMENT_GROUP_URL,
  IDVALIDATOR_URL,
  MANUALREQUESTQUEUE_URL,
  CASE_UPLOAD_URL,
  PROFORMA_UPLOAD,
  POWERBI_URL,
  LOBBY_MESSAGE_URL,
  PAYWALLREQUESTQUEUE_URL,
  TEAM_CASES_URL,
  OVERVIEW_CASES_URL,
  TAXCHAT_SERVICE_INITIATION_URL,
  CONSENT_MANAGEMENT_URL,
} from 'src/constants/constants.js';
import { isTaxChatSuperAdmin  } from 'src/helpers';
import ServiceSpinner from 'src/ui/library/ServiceSpinner';
import styled from 'src/ui/library/styled';

const styles = theme => ({
  root: {
    backgroundColor: theme.colors.eyConfidentGrey,
  }
});

const GlobalMenuPres = ({
  isMenuOpen,
  toggleDrawer,
  isApplicant,
  isEYPro,
  isEYAdmin,
  isDeleteProfile,
  classes
}) => {
  const accessAllowed = (isEYPro || isEYAdmin || isDeleteProfile || isTaxChatSuperAdmin());
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const [isNavigationPending, setIsNavigationPending] = useState(false);
  const handleOpenDrawer = event => toggleDrawer(true)(event);
  const handleCloseDrawer = event => {
    if (menuButtonRef.current && typeof menuButtonRef.current.focus === 'function') menuButtonRef.current.focus();
    toggleDrawer(false)(event);
  };
  const handleMenuNavigation = route => {
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (route === currentRoute || route === POWERBI_URL) return;

    flushSync(() => setIsNavigationPending(true));
  };

  useEffect(() => {
    setIsNavigationPending(false);
  }, [location.pathname, location.search, location.hash]);

  return (
    <Fragment>
      {isNavigationPending && <ServiceSpinner open={isNavigationPending} />}
      <div className="header-button menu" ref={menuButtonRef} tabIndex={-1}>
        <Menu className="icon lg" onClick={handleOpenDrawer} />
      </div>
      <Drawer classes={{ paper: classes.root}} anchor="right" open={isMenuOpen} onClose={handleCloseDrawer}>
        <div tabIndex={0} role="button" onClick={handleCloseDrawer} onKeyDown={handleCloseDrawer} className="global-menu-container">
          <div className="icon-menu nav-bar">
            {/* <GlobalMenuIcon route={'/'} text={intl.get('globalMenu.myProfile')}> */}
            <span className="link-icon anchor-cursor">
              <AccountCircle className="icon lg" />
              <label className="icon-label">{intl.get('globalMenu.myProfile')}</label>
            </span>
            {/* </GlobalMenuIcon> */}
            <GlobalMenuIcon route={HELP_URL} text={intl.get('globalMenu.help')}>
              <HelpOutline className="icon lg" />
            </GlobalMenuIcon>
            <GlobalMenuIcon route={LOGOUT_URL} text={intl.get('globalMenu.logout')} className="logout">
              <Logout className="icon lg" />
            </GlobalMenuIcon>
          </div>
          <div className="text-menu">
            <nav className="nav-bar">
              <Clear className="icon lg" onClick={handleCloseDrawer} />
            </nav>
            <GlobalMenuList>
              <React.Fragment>
                <GlobalMenuItem route={DASHBOARD_URL} text={intl.get('globalMenu.dashboard')} onNavigate={handleMenuNavigation} />
                {!isApplicant && <GlobalMenuItem route={CASES_URL}  text={intl.get('globalMenu.myCases')} onNavigate={handleMenuNavigation} />}
                {!isApplicant && <GlobalMenuItem route={TEAM_CASES_URL}  text={intl.get('globalMenu.teamCases')} onNavigate={handleMenuNavigation} />}
                {!isApplicant && <GlobalMenuItem route={OVERVIEW_CASES_URL}  text={intl.get('globalMenu.overviewCases')} onNavigate={handleMenuNavigation} />}
                {!isApplicant && <GlobalMenuItem route={TAXCHAT_SERVICE_INITIATION_URL} text={intl.get('globalMenu.serviceInitiation')} onNavigate={handleMenuNavigation} />}
                {/* {!isApplicant ||
                  (!isCorporateClientLineManagerSelector && (
                    <GlobalMenuItem route={SERVICEINIT_URL} text={intl.get('globalMenu.serviceInitiation')} />
                  ))} */}
                <GlobalMenuItem route={DOCUMENTS_URL} text={intl.get('globalMenu.documents')} onNavigate={handleMenuNavigation} />
                <GlobalMenuItem route={POWERBI_URL} text={intl.get('globalMenu.powerbi')} onNavigate={handleMenuNavigation} />
                {isDeleteProfile &&
                <GlobalMenuItem route={LOBBY_MESSAGE_URL} text={intl.get('globalMenu.lobbyMessage')} onNavigate={handleMenuNavigation} />}
                {(isEYAdmin || isEYPro || isDeleteProfile || isTaxChatSuperAdmin()) && (
                  <GlobalMenuGroup text={intl.get('globalMenu.administration')}>
                    {isEYAdmin && <GlobalMenuItem route={USERS_URL} text={intl.get('globalMenu.users')} onNavigate={handleMenuNavigation} />}
                    {isDeleteProfile && <GlobalMenuItem route={CONSENT_MANAGEMENT_URL} text={intl.get('globalMenu.consentManagement')} onNavigate={handleMenuNavigation} />}
                    {(isDeleteProfile || isTaxChatSuperAdmin()) && <GlobalMenuItem route={ENGAGEMENT_GROUP_URL} text={intl.get('globalMenu.engagementGroup')} onNavigate={handleMenuNavigation} />}
                    <GlobalMenuItem route={ENGAGEMENTS_URL} text={intl.get('globalMenu.engagements')} onNavigate={handleMenuNavigation} />
                    {isDeleteProfile && <GlobalMenuItem route={UPLOADS} text={intl.get('globalMenu.cmsUploads')} onNavigate={handleMenuNavigation} />}
                    {accessAllowed && <GlobalMenuItem route={PREREGISTER_APPLICANTS_URL} text={intl.get('globalMenu.bulkTemplateUpdates')} onNavigate={handleMenuNavigation} />}
                    {isEYAdmin && <GlobalMenuItem route={CASE_UPLOAD_URL} text={intl.get('globalMenu.caseUpload')} onNavigate={handleMenuNavigation} />}
                    {isEYAdmin && <GlobalMenuItem route={PROFORMA_UPLOAD} text={intl.get('globalMenu.proformaUpload')} onNavigate={handleMenuNavigation} />}
                    {isEYAdmin && <GlobalMenuItem route={IDVALIDATOR_URL} text={intl.get('globalMenu.idValidator')} onNavigate={handleMenuNavigation} />}
                    {isEYAdmin && <GlobalMenuItem route={MANUALREQUESTQUEUE_URL} text={intl.get('globalMenu.manualRequestQueue')} onNavigate={handleMenuNavigation} />}
                    {isDeleteProfile && <GlobalMenuItem route={PAYWALLREQUESTQUEUE_URL} text={intl.get('globalMenu.paywallRequestQueue')} onNavigate={handleMenuNavigation} />}
                  </GlobalMenuGroup>
                )}
              </React.Fragment>
            </GlobalMenuList>
          </div>
        </div>
      </Drawer>
    </Fragment>
  );
};

GlobalMenuPres.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
  }).isRequired,
  isApplicant: PropTypes.bool.isRequired,
  isDeleteProfile: PropTypes.bool.isRequired,
  isEYAdmin: PropTypes.bool.isRequired,
  isEYPro: PropTypes.bool.isRequired,
  isMenuOpen: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};

export default styled(GlobalMenuPres)(styles);
